import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { QueryRef } from 'apollo-angular';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ResponsiveService } from 'src/app/core/services/admin/responsive.service';
import { BookingService } from 'src/app/core/services/booking/booking.service';
import { FlightService } from 'src/app/core/services/flight/flight.service';
import { DialogEmailComponent } from 'src/app/features/dialog/dialog-email/dialog-email.component';

// Cadence for background refresh while at least one mail has no final result yet.
// SendGrid processed events arrive in ~1-3 s, delivered events typically inside ~30 s,
// so a 4 s cadence resolves the "in transmission" label fast without hammering the API.
const MAIL_REFRESH_INTERVAL_MS = 4000;

@Component({
	selector: 'app-booking-mail-card',
	templateUrl: './booking-mail-card.component.html',
	styleUrls: ['./booking-mail-card.component.scss'],
})
export class BookingMailCardComponent implements OnInit, OnDestroy {
	@Input() bookingId: string;

	mails = [];
	//responsive
	isMobile: boolean;
	subs: Subscription[] = [];
	private destroy$ = new Subject<void>();
	private mailsQueryRef: QueryRef<any> | null = null;
	private isPolling = false;

	constructor(private bookingService: BookingService, private responsiveService: ResponsiveService, private dialog: MatDialog) {
		// responsiveService Subscriptions
		this.responsiveService.viewMobileChanged.pipe(takeUntil(this.destroy$)).subscribe((mobileView) => {
			this.isMobile = mobileView;
		});
	}

	ngOnInit(): void {
		if (this.bookingId) {
			this.mailsQueryRef = this.bookingService.getBookingMails(this.bookingId);
			const mailSub = this.mailsQueryRef.valueChanges.subscribe((result) => {
				this.mails = result.data.booking.mails;
				const mailsMerged = result.data.booking.mailsMerged.map((mail) => {
					// add a merge flag to each log
					return { ...mail, merge: true };
				});
				this.mails = this.mails.concat(mailsMerged);
				// sort this mails by date
				this.mails.sort((a, b) => {
					const dateA = new Date(a.resultAt);
					const dateB = new Date(b.resultAt);
					return dateA < dateB ? 1 : -1;
				});
				// Auto-refresh while at least one mail has no final result yet
				// (FR-001/FR-002/FR-004). The accordion uses matExpansionPanelContent so this
				// component is destroyed when the accordion is closed, satisfying FR-003.
				this.updatePollingState();
			});
			this.subs.push(mailSub);
		}
	}

	ngOnDestroy(): void {
		this.stopPolling();
		this.subs.forEach((sub) => sub.unsubscribe());
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * A mail has a final result once `resultAt` is recorded (either delivered/sent or error).
	 * While `resultAt` is absent the UI shows "en cours de transmission".
	 */
	private hasPendingMail(): boolean {
		return this.mails.some((mail) => !mail?.resultAt);
	}

	private updatePollingState(): void {
		if (this.hasPendingMail()) {
			this.startPolling();
		} else {
			this.stopPolling();
		}
	}

	private startPolling(): void {
		if (this.isPolling || !this.mailsQueryRef) return;
		this.mailsQueryRef.startPolling(MAIL_REFRESH_INTERVAL_MS);
		this.isPolling = true;
	}

	private stopPolling(): void {
		if (!this.isPolling || !this.mailsQueryRef) return;
		this.mailsQueryRef.stopPolling();
		this.isPolling = false;
	}

	showMail(email) {
		const emails = [email];
		if (emails.length === 0) return;
		const dialogRef = this.dialog.open(DialogEmailComponent, {
			width: window.innerWidth <= 360 ? '95%' : '80%',
			maxWidth: 'none',
			data: {
				emails: emails,
				readOnly: true,
			},
		});
	}

	badgeColor(events) {
		let color = 'primary';
		events.forEach((event) => {
			if (event.event === 'dropped') color = 'warn';
			if (event.event === 'bounce') color = 'warn';
			if (event.event === 'blocked') color = 'warn';
		});
		return color;
	}

	sortMailEvents(events) {
		// make sure events are shown in the right order, even with the same timestamp

		// add 1s to timestamp in case of problem events, event = dropped, bounced, blocked
		// add 2s to timestamp in case of delivered events, event = delivered
		// then sort by timestamp
		// make a new array to avoid changing the original array
		const sortedEvents = events.map((event) => {
			let timestamp = new Date(event.timestamp);
			if (event.event === 'dropped' || event.event === 'bounce' || event.event === 'blocked') {
				timestamp = new Date(timestamp.getTime() + 1000);
			}
			if (event.event === 'delivered') {
				timestamp = new Date(timestamp.getTime() + 2000);
			}
			return { ...event, timestamp: timestamp };
		});
		return sortedEvents.sort((a, b) => {
			return a.timestamp < b.timestamp ? -1 : 1;
		});
	}
}
